import { useTranslation } from "react-i18next";

import IntegrationErrorIcon from "@/assets/customIcons/IntegrationErrorIcon";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import { ALERT_STATUS } from "@/context/alert/types";
import { IntegrationsErrorDialogSteps, useMediaQueries } from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import useResponse from "@/shared/hooks/useResponse";
import { useIntegrationsDialogStore } from "@/stores/useIntegrationsDialogStore";

interface IntegrationErrorDialogProps {
  connectionErrorCode: number;
  isOpen: boolean;
  onEditResource: () => void;
  handleErrorClose: () => void;
}

const IntegrationErrorDialog = ({
  connectionErrorCode,
  isOpen,
  onEditResource,
  handleErrorClose
}: IntegrationErrorDialogProps) => {
  const { toMd } = useMediaQueries();
  const { t } = useTranslation();
  const { handleResponse } = useResponse();

  const sourceStep = useIntegrationsDialogStore(state => state.sourceStep);

  const errorCodeMap: Record<number, string> = {
    1: "HostUnreachable",
    2: "AuthenticationFailed",
    3: "PermissionDenied",
    4: "PathNotFound",
    5: "FolderCreationFailed",
    99: "Unknown"
  };

  const errorKey = errorCodeMap[connectionErrorCode];
  const title = t(`Dialog##integrations##errors##${errorKey}##title`);
  const description = t(
    `Dialog##integrations##errors##${errorKey}##description`
  );

  const getSuccessMessage = () => {
    if (sourceStep === IntegrationsErrorDialogSteps.Add) {
      return t("Alert##add integration success");
    }

    if (sourceStep === IntegrationsErrorDialogSteps.Edit) {
      return t("Alert##edit integration success");
    }

    return null;
  };

  const handleClose = () => {
    handleErrorClose();

    const sucessMessage = getSuccessMessage();

    sucessMessage && handleResponse(ALERT_STATUS.Success, sucessMessage);
  };

  const handleEditClick = () => {
    onEditResource();
  };

  return (
    <Dialog
      data-testid="integration-error-dialog"
      titleImage={<IntegrationErrorIcon sx={{ fontSize: "120px" }} />}
      title={title}
      description={description}
      isActionsSeparator
      extendToMaxWidth={toMd}
      open={isOpen}
      close={handleClose}
      submit={handleEditClick}
      width={toMd ? undefined : DialogWidth.MinWidth}
      actions={
        <DialogActions
          isreadyToConfirm
          onClose={handleClose}
          isLoading={false}
          confirmationButtonText={t("Button##edit integration")}
          confirmationButtonTestId="edit-integration-error-button"
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="close-integration-error-button"
        />
      }
    />
  );
};

export default IntegrationErrorDialog;
